import { useState } from "react";
import { ChevronLeft, ChevronRight, Play, Star } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { media } from "@/lib/api";

const LIGHT = "bg-[#F2EBDD] text-[#131413]";

export const Glimpse = ({ c = {} }) => {
  const [playing, setPlaying] = useState(false);
  return (
    <section className="mx-auto max-w-[1440px] px-5 py-16 sm:px-20 sm:py-20" data-testid="glimpse-section" aria-label={c.label}>
      <p className="font-body flex items-center gap-3 text-[12px] font-medium uppercase tracking-[0.2em] text-[#C9A66B]"><span className="h-px w-8 bg-[#C9A66B]" />{c.label}</p>
      <div className="relative mt-6 aspect-video overflow-hidden bg-black">
        {c.video && playing ? (
          <video src={media(c.video)} poster={media(c.poster)} controls autoPlay playsInline className="h-full w-full object-cover" data-testid="glimpse-video" />
        ) : (
          <>
            <img src={media(c.poster)} alt="Guests aboard the carriage" loading="lazy" className="h-full w-full object-cover" />
            {c.video && (
              <button type="button" onClick={() => setPlaying(true)} aria-label="Play video" className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/70 bg-black/40 text-white backdrop-blur transition-transform hover:scale-105" data-testid="glimpse-play-button">
                <Play size={24} fill="currentColor" aria-hidden="true" />
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export const StoryCarousel = ({ c = {} }) => {
  const imgs = Array.isArray(c.images) ? c.images.filter(Boolean) : [];
  const [i, setI] = useState(0);
  const go = (d) => setI((v) => (v + d + imgs.length) % imgs.length);
  return (
    <section className={LIGHT} data-testid="story-section">
      <div className="mx-auto grid max-w-[1440px] gap-10 px-5 py-20 sm:px-20 md:grid-cols-[0.8fr_1.2fr] md:items-center md:gap-16 md:py-28">
        <Reveal>
          <h2 className="font-head text-3xl sm:text-4xl lg:text-[56px]">{c.heading}</h2>
          <p className="font-body mt-6 max-w-md text-[15px] leading-[1.5] text-[#131413]/85 md:text-[18px]">{c.text}</p>
        </Reveal>
        {imgs.length > 0 && (
          <Reveal className="relative">
            <div className="relative aspect-[3/2] overflow-hidden bg-[#131413]">
              {imgs.map((src, k) => (
                <img key={src + k} src={media(src)} alt="" loading="lazy" className={`absolute inset-0 h-full w-full object-cover grayscale transition-opacity duration-700 ${k === i ? "opacity-100" : "opacity-0"}`} />
              ))}
              {imgs.length > 1 && [[-1, ChevronLeft, "left-3", "Previous photo"], [1, ChevronRight, "right-3", "Next photo"]].map(([d, Icon, pos, label]) => (
                <button key={label} type="button" onClick={() => go(d)} aria-label={label} className={`absolute top-1/2 ${pos} flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-[#131413] hover:bg-white`} data-testid={`story-carousel-${d < 0 ? "prev" : "next"}`}>
                  <Icon size={18} aria-hidden="true" />
                </button>
              ))}
            </div>
            <div className="mt-4 flex justify-center gap-2">
              {imgs.map((_, k) => <button key={k} type="button" onClick={() => setI(k)} aria-label={`Photo ${k + 1}`} className="flex h-6 w-6 items-center justify-center"><span className={`h-2 w-2 rounded-full ${k === i ? "bg-[#131413]" : "bg-[#131413]/30"}`} /></button>)}
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};

export const Tillys = ({ c = {} }) => (
  <section className="relative overflow-hidden" data-testid="tillys-section">
    <div className="tillys-fade absolute inset-y-0 left-0 w-full md:w-[68%]">
      <img src={media(c.image)} alt="A singer in a feathered headdress at Tilly's Music Hall" loading="lazy" className="h-full w-full object-cover object-[40%_center]" data-testid="tillys-bg-image" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-b from-[#131413]/85 via-[#131413]/30 to-[#131413]/90 md:from-[#131413]/50 md:via-transparent md:to-[#131413]/40" aria-hidden="true" />
    <div className="relative mx-auto flex min-h-[760px] max-w-[1440px] flex-col px-5 pb-14 pt-16 sm:px-20 md:min-h-[960px] md:pb-24">
      <div className="grid gap-6 sm:grid-cols-3">
        {(c.quotes || []).map((q, k) => (
          <Reveal key={k} delay={k * 0.06}>
            <blockquote className="border-l border-[#C9A66B]/60 pl-4">
              <p className="font-head text-sm sm:text-base">&ldquo;{q.quote}&rdquo;</p>
              <cite className="font-body mt-2 block text-[11px] not-italic uppercase tracking-[0.16em] text-[#F2EBDD]/70">{q.source}</cite>
            </blockquote>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-auto md:mb-auto md:ml-[48%] md:mt-[22vh] md:max-w-[640px]">
        <h2 className="font-head text-3xl lg:text-[56px]">{c.heading}</h2>
        <p className="font-body mt-6 max-w-lg text-[15px] leading-[1.5] text-[#F2EBDD]/90 md:text-[18px]">{c.text}</p>
      </Reveal>
    </div>
  </section>
);

const Overlay = ({ title, text }) => (title || text) ? (
  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0D0C09]/90 via-[#0D0C09]/50 to-transparent px-5 pb-6 pt-24 text-[#F2EBDD]">
    {title && <p className="font-head text-lg lg:text-xl">{title}</p>}
    {text && <p className="font-body mt-2 text-[14px] text-[#F2EBDD]/90">{text}</p>}
  </div>
) : null;

const items = (v) => (Array.isArray(v) ? v.filter((x) => x?.image) : []);

const TasteCarousel = ({ slides }) => {
  const [i, setI] = useState(0);
  const [x0, setX0] = useState(null);
  const n = slides.length;
  const go = (d) => setI((v) => (v + d + n) % n);
  if (!n) return null;
  return (
    <div className="mt-10 md:hidden" data-testid="taste-mobile-carousel">
      <div className="relative aspect-[4/5] overflow-hidden bg-[#131413]" onTouchStart={(e) => setX0(e.touches[0].clientX)}
        onTouchEnd={(e) => { if (x0 === null) return; const dx = e.changedTouches[0].clientX - x0; if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1); setX0(null); }}>
        {slides.map((s, k) => (
          <div key={k} className={`absolute inset-0 transition-opacity duration-500 ${k === i ? "opacity-100" : "pointer-events-none opacity-0"}`} aria-hidden={k !== i} data-testid={`taste-mobile-slide-${k}`}>
            <img src={media(s.image)} alt={s.title || "A dish from the menu"} loading="lazy" className="h-full w-full object-cover" />
            <Overlay title={s.title} text={s.text} />
          </div>
        ))}
        {n > 1 && [[-1, ChevronLeft, "left-3", "Previous dish"], [1, ChevronRight, "right-3", "Next dish"]].map(([d, Icon, pos, label]) => (
          <button key={label} type="button" onClick={() => go(d)} aria-label={label} className={`absolute top-1/2 ${pos} flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/85 text-[#131413]`} data-testid={`taste-carousel-${d < 0 ? "prev" : "next"}`}>
            <Icon size={18} aria-hidden="true" />
          </button>
        ))}
      </div>
      {n > 1 && <div className="mt-4 flex justify-center gap-2">
        {slides.map((_, k) => <button key={k} type="button" onClick={() => setI(k)} aria-label={`Dish ${k + 1}`} className="flex h-6 w-6 items-center justify-center"><span className={`h-2 w-2 rounded-full ${k === i ? "bg-[#131413]" : "bg-[#131413]/30"}`} /></button>)}
      </div>}
    </div>
  );
};

export const Taste = ({ c = {} }) => {
  const cards = items(c.cards);
  return (
    <section className={LIGHT} data-testid="taste-section">
      <div className="mx-auto max-w-[1440px] px-5 py-20 sm:px-20 md:py-24">
        <div className="grid gap-6 md:grid-cols-2 md:items-start">
          <Reveal><h2 className="font-head text-3xl sm:text-4xl lg:text-[56px]">{c.heading}</h2></Reveal>
          <Reveal delay={0.06}><p className="font-body max-w-sm text-[15px] leading-[1.5] text-[#131413]/85 md:ml-auto md:text-[18px]">{c.text}</p></Reveal>
        </div>
        {cards.length > 0 && (
          <div className="mt-10 hidden gap-4 md:grid" style={{ gridTemplateColumns: `repeat(${cards.length}, minmax(0, 1fr))` }} data-testid="taste-desktop-grid">
            {cards.map((s, k) => (
              <div key={k} className="relative aspect-[4/9] overflow-hidden bg-[#131413]" data-testid={`taste-card-${k}`}>
                <img src={media(s.image)} alt={s.title || "A dish from the menu"} loading="lazy" className="h-full w-full object-cover" />
                <Overlay title={s.title} text={s.text} />
              </div>
            ))}
          </div>
        )}
        <TasteCarousel slides={items(c.mobile_slides).length ? items(c.mobile_slides) : cards} />
      </div>
    </section>
  );
};

export const Team = ({ c = {} }) => (
  <section className="pb-16" data-testid="team-section">
    <div className="relative overflow-hidden">
      <div className="team-fade absolute inset-y-0 right-0 w-full md:w-[62%]" data-testid="team-fade">
        <img src={media(c.image)} alt="Actors from The Murdér Express" loading="lazy" className="h-full w-full object-cover object-[center_62%] grayscale" data-testid="team-bg-image" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#131413]/60 to-transparent md:hidden" aria-hidden="true" />
      </div>
      <Reveal className="relative mx-auto flex min-h-[720px] max-w-[1440px] flex-col justify-end px-5 pb-12 pt-40 sm:px-20 md:pb-20">
        <div className="md:max-w-3xl">
          <h2 className="font-head text-2xl sm:text-3xl lg:text-[56px]">{c.heading}</h2>
          <p className="font-head mt-6 text-lg text-[#C9A66B] sm:text-xl" data-testid="team-rating">{c.rating}</p>
        </div>
      </Reveal>
    </div>
    <div className="mx-auto mt-4 grid max-w-[1440px] gap-4 px-5 sm:grid-cols-2 sm:px-20 lg:grid-cols-3">
      {(c.reviews || []).map((r, k) => (
        <Reveal key={k} delay={(k % 3) * 0.05}>
          <figure className="flex h-full flex-col justify-between border border-[#F2EBDD]/20 p-6" data-testid={`review-card-${k}`}>
            <div>
              <div className="flex gap-1 text-[#C9A66B]" aria-label="Five stars">{[0, 1, 2, 3, 4].map((s) => <Star key={s} size={14} fill="currentColor" aria-hidden="true" />)}</div>
              <blockquote className="font-body mt-4 text-base leading-relaxed">&ldquo;{r.quote}&rdquo;</blockquote>
            </div>
            <figcaption className="font-body mt-8 text-[13px] text-[#F2EBDD]/70">{r.author}</figcaption>
          </figure>
        </Reveal>
      ))}
    </div>
  </section>
);
